"use client";

import React from "react";
import { Shield, Camera, Video, Activity, Lock, CircleCheck, CircleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const securityFeatures = [{
  title: "Εγκατάσταση καμερών υψηλής ευκρίνειας (CCTV)",
  description: "Κρυστάλλινη εικόνα μέρα και νύχτα για πλήρη εποπτεία του χώρου σας."
}, {
  title: "Συστήματα ελέγχου πρόσβασης (Access Control)",
  description: "Διαχειριστείτε ποιος εισέρχεται στις εγκαταστάσεις σας με ακρίβεια."
}, {
  title: "24/7 παρακολούθηση και καταγραφή",
  description: "Αδιάλειπτη λειτουργία και ασφαλής αποθήκευση δεδομένων."
}, {
  title: "Άμεση ειδοποίηση παραβίασης",
  description: "Λάβετε ειδοποιήσεις σε πραγματικό χρόνο στο κινητό σας."
}];
const accessLogs = [{
  time: "08:15:22",
  user: "Γ. Παπαδόπουλος",
  action: "Είσοδος",
  status: "Εγκρίθηκε",
  icon: CircleCheck,
  color: "text-primary"
}, {
  time: "08:42:10",
  user: "Μ. Αντωνίου",
  action: "Είσοδος",
  status: "Εγκρίθηκε",
  icon: CircleCheck,
  color: "text-primary"
}, {
  time: "09:05:45",
  user: "Άγνωστος",
  action: "Απόπειρα",
  status: "Απορρίφθηκε",
  icon: CircleAlert,
  color: "text-destructive"
}, {
  time: "09:12:30",
  user: "Κ. Δημητρίου",
  action: "Είσοδος",
  status: "Εγκρίθηκε",
  icon: CircleCheck,
  color: "text-primary"
}];
export default function AbsoluteSecurityControl() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: uiRef,
    isVisible: isUiVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="απόλυτη-ασφάλεια-έλεγχος" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/modern_office_security_bg.png')] bg-cover bg-center opacity-30 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div ref={textRef} className={`space-y-8 transition-all duration-1000 ${isTextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-4">
              <Lock className="size-4" />
              <span>Φυσική Ασφάλεια & Προστασία</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
              Απόλυτη Ασφάλεια & <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Έλεγχος</span>
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
              Η <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/ds_services_logo.png" alt="Ds-Services" className="h-7 inline-block align-middle" /> θωρακίζει τον επαγγελματικό σας χώρο. Προσφέρουμε ολοκληρωμένες λύσεις φυσικής ασφάλειας, από προηγμένα συστήματα καμερών μέχρι έξυπνο έλεγχο πρόσβασης, εξασφαλίζοντας την ηρεμία σας 24/7.
            </p>

            <div className="space-y-6 pt-4">
              {securityFeatures.map((feature, index) => <div key={index} data-index={index} className="flex gap-4 items-start">
                  <div className="mt-1 bg-primary/10 p-2 rounded-lg border border-primary/20 shrink-0">
                    <Shield className="size-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-lg">{feature.title}</h3>
                    <p className="text-muted-foreground mt-1">{feature.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-8">
              <Button asChild size="lg" className="gap-2 shadow-[0_0_20px_rgba(var(--color-primary),0.3)]">
                <Link to="/ContactPage">
                  Θωρακίστε την επιχείρησή σας
                  <Activity className="size-4" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={uiRef} className={`relative h-[600px] transition-all duration-1000 delay-300 ${isUiVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {}
            <Card className="absolute top-0 right-0 w-full max-w-md bg-card/40 backdrop-blur-xl border-primary/30 shadow-[0_8px_32px_rgba(0,0,0,0.4)] overflow-hidden z-20">
              <CardHeader className="pb-2 border-b border-border/50 bg-muted/20 flex flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-2">
                  <Video className="size-4 text-primary" />
                  <CardTitle className="text-sm font-mono text-muted-foreground">CAM_01_MAIN_ENTRANCE</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive"></span>
                  </span>
                  <span className="text-xs font-mono text-destructive font-bold">REC</span>
                </div>
              </CardHeader>
              <CardContent className="p-0 relative aspect-video bg-black">
                <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/security_camera_feed.png')] bg-cover bg-center opacity-60" />
                {}
                <div className="absolute inset-0 border-[1px] border-primary/20 m-4 pointer-events-none flex flex-col justify-between p-2">
                  <div className="flex justify-between text-[10px] font-mono text-primary/70">
                    <span>192.168.1.104</span>
                    <span>{new Date().toLocaleDateString('el-GR')}</span>
                  </div>
                  <div className="flex justify-center items-center h-full">
                    <div className="w-16 h-16 border border-primary/30 rounded-full flex items-center justify-center">
                      <div className="w-1 h-1 bg-primary/50 rounded-full" />
                    </div>
                  </div>
                  <div className="flex justify-between text-[10px] font-mono text-primary/70">
                    <span>1080p / 30fps</span>
                    <span>ZOOM: 1.0x</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {}
            <Card className="absolute bottom-10 left-0 w-full max-w-md bg-card/60 backdrop-blur-md border-border/50 shadow-2xl z-30 transform -translate-x-4 lg:-translate-x-12">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Activity className="size-5 text-secondary" />
                  <CardTitle className="text-base">Αρχείο Πρόσβασης (Live)</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50 hover:bg-transparent">
                      <TableHead className="text-xs text-muted-foreground">Ώρα</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Χρήστης</TableHead>
                      <TableHead className="text-xs text-muted-foreground text-right">Κατάσταση</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accessLogs.map((log, index) => <TableRow key={index} data-index={index} className="border-border/20 hover:bg-muted/20">
                        <TableCell className="font-mono text-xs text-muted-foreground py-2">{log.time}</TableCell>
                        <TableCell className="py-2 text-sm">{log.user}</TableCell>
                        <TableCell className="py-2 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <span className={`text-xs font-medium ${log.color}`}>{log.status}</span>
                            <log.icon className={`size-3.5 ${log.color}`} />
                          </div>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-primary/5 rounded-full blur-3xl -z-10 pointer-events-none" />
          </div>

        </div>
      </div>
    </section>;
}